import { EmptyBagMessageCard } from "@/components/Checkout/EmptyBagMessageCard";
import LandingPageWrapper from "@/components/pages/AppPages/Pages/components/LandingPageWrapper";

import { styled } from "@mui/material";

import { CheckoutContext } from "../checkoutUIMachine";
import { useCheckoutUI } from "../CheckoutUIProvider";
import { useCheckoutCartInfo } from "../hooks/useCheckoutCartInfo";

import CollapsibleSection from "@/components/Checkout/components/CheckoutV2/CheckoutForm/components/CollapsibleSection";
import { PaymentMethodDisplay } from "@/components/Checkout/components/CheckoutV2/CheckoutForm/components/PaymentMethodDisplay";

import Loading from "@/components/Loading";
import { useAppSelector } from "@/redux/hooks";
import { useTrackViewEvent } from "@/utils/hooks/useTracking";
import {
  AnalyticsTrackingEvent,
  brandclub_colors,
  getEntityImage,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { useCallback, useEffect } from "react";
import CheckoutAccountInfo from "./components/accountInfo/CheckoutAccountInfo";
import { StatusIcon } from "./components/common/StatusIcons";
import OrderSummary, {
  FloatingOrderSummary,
} from "./components/orderSummary/OrderSummary";
import { ExperimentalCheckoutPaymentEditForm } from "./components/payment/CheckoutPaymentEditForm";

import { ShippingMethodForm } from "./components/shipping/ShippingMethodForm";
import CheckoutSignIn from "./components/signIn/CheckoutSignIn";

import AccountForCheckout from "./components/account";

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  height: "100%",
  gap: "10px",
  marginTop: "68px",
  flexDirection: "row",
  justifyContent: "space-between",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    flexDirection: "column",
    justifyContent: "flex-start",
    marginTop: "24px",
  },

  ".checkoutSubmitForm": {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {
      marginBottom: "80px",
    },
    ".checkoutSubmitForm_signin": {
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.down("md")]: {},
    },
    width: "100%",
    maxWidth: 776,
    marginRight: 48,
    ".collapsible-section + .collapsible-section": {
      borderTop: `1px solid ${brandclub_colors.grayLight}`,
    },
    ".checkoutTitle": {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: 28,
      fontWeight: "bold",
      paddingBottom: 5,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        fontSize: 22,
      },
    },
  },
  ".sideSummary": {
    display: "block",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      display: "none",
    },
  },

  ".title": {
    fontWeight: "bold",
    fontSize: 22,
    paddingBottom: 4,
  },

  ".mobileTotalAndButton": {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: "48px",
  },
}));

export const FormSectionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
}));

const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  margin: "auto",
  maxHeight: 250,
  maxWidth: "90%",
});

const Divider = styled("div")(({ theme }) => ({
  display: "none",
  width: "100%",
  height: "1px",
  backgroundColor: "#dcdddc",
  margin: "14px 0 2px 0",
  [theme.breakpoints.down("md")]: {
    display: "block",
  },
}));

const PreviewContainer = styled("div")(({ theme }) => ({
  marginTop: "16px",
  fontSize: 16,
  [theme.breakpoints.down("md")]: {
    fontSize: 14,
  },
}));

const CheckoutForm = () => {
  const userProfile = useAppSelector((state) => state.userProfile);
  const [state, send] = useCheckoutUI();
  const { items } = useCheckoutCartInfo();
  const trackCheckoutPageView = useTrackViewEvent(
    AnalyticsTrackingEvent.CHECKOUT_PAGE_VIEW
  );
  const trackPageView = useTrackViewEvent(AnalyticsTrackingEvent.PAGE_VIEW);
  const pageView = useCallback(
    (additionalData: Record<string, unknown>) => {
      trackPageView(additionalData);
      trackCheckoutPageView(additionalData);
    },
    [trackCheckoutPageView, trackPageView]
  );

  const {
    payment,
    shipping,
    checkoutIntent,
    retailerLogin,
    checkoutIntentLoading,
    checkoutResponse,
  } = state.context as unknown as CheckoutContext;

  const orderId = checkoutResponse?.userRetailerCheckoutOrder?.orderId;
  const isPostSubmission = !!orderId;
  useEffect(() => {
    const eventParams = {
      checkoutIntentId: checkoutIntent?.checkoutIntentId,
      checkoutItems: items,
      checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
      ...(orderId && { orderId }),
    };
    pageView(eventParams);
  }, [pageView, checkoutIntent, isPostSubmission, orderId, items]);

  const shippingInput = checkoutIntent?.shippingAddress;
  const loading = false;

  const toggleSection = (
    step: keyof Pick<CheckoutContext, "shipping" | "payment" | "retailerLogin">
  ) => {
    send({ type: "TOGGLE", data: { section: step } });
  };

  if (!userProfile?.userId) {
    return (
      <LandingPageWrapper includeFooter={false}>
        <Container>
          <div className="checkoutSubmitForm_signin">
            <CheckoutSignIn />
          </div>
          <Divider />
          <div
            className="sideSummary"
            style={{
              display: "block",
            }}
          >
            <OrderSummary />
          </div>
        </Container>
      </LandingPageWrapper>
    );
  }

  if (checkoutIntentLoading) {
    return <Loading dot />;
  }

  if (!items.length) {
    return <StyledEmptyBagMessageCard />;
  }

  const currentRetailerId = checkoutIntent?.retailerId;

  return (
    <LandingPageWrapper includeFooter={false}>
      <Container>
        <div className="checkoutSubmitForm">
          <div className="checkoutTitle">
            {currentRetailerId ? (
              <img
                src={getEntityImage(currentRetailerId ?? 0, "retailer")}
                alt="Retailer Logo"
                style={{
                  width: "28px",
                  height: "28px",
                }}
              />
            ) : null}
            Checkout
          </div>
          <CollapsibleSection
            hideControl={true}
            isOpen={true}
            disabled={false}
            loading={false}
            title={"Contact"}
            titleIcon={
              <StatusIcon success={true} errorColor="rgb(157, 56, 65)" />
            }
          >
            <CheckoutAccountInfo />
          </CollapsibleSection>

          {state.value !== "initial" ? (
            <>
              {payment.visible && (
                <CollapsibleSection
                  isOpen={payment.open}
                  loading={loading}
                  disabled={!payment.canOpen}
                  onToggle={() => {
                    toggleSection("payment");
                  }}
                  titleIcon={
                    <StatusIcon
                      success={!payment?.forceComplete}
                      errorColor="rgb(157, 56, 65)"
                    />
                  }
                  title={"Payment method"}
                  collapsedContent={
                    <PreviewContainer>
                      {checkoutIntent?.stripePaymentMethod ? (
                        <PaymentMethodDisplay
                          paymentMethod={checkoutIntent.stripePaymentMethod}
                        />
                      ) : (
                        "Enter your preferred payment method"
                      )}
                    </PreviewContainer>
                  }
                  style={{
                    opacity:
                      checkoutIntent?.stripePaymentMethod ||
                      payment.open ||
                      payment.canOpen
                        ? 1
                        : 0.5,
                  }}
                >
                  <ExperimentalCheckoutPaymentEditForm
                    formVisible={payment.open}
                  />
                </CollapsibleSection>
              )}

              {!payment.forceComplete && shipping.visible && (
                <CollapsibleSection
                  isOpen={shipping.open}
                  disabled={!shipping.canOpen}
                  loading={loading}
                  title={"Shipping address"}
                  titleIcon={
                    <StatusIcon
                      success={!shipping.forceComplete}
                      errorColor="rgb(157, 56, 65)"
                    />
                  }
                  onToggle={() => {
                    toggleSection("shipping");
                  }}
                  style={{
                    opacity: shipping.open || shipping.canOpen ? 1 : 0.5,
                  }}
                  collapsedContent={
                    <PreviewContainer>
                      {shipping.forceComplete ? (
                        "Enter your preferred shipping address"
                      ) : (
                        <div>
                          <div>
                            {shippingInput?.firstName} {shippingInput?.lastName}
                          </div>
                          <div>
                            {shippingInput?.address1}, {shippingInput?.address2}
                            {shippingInput?.address2 ? "," : ""}{" "}
                            {shippingInput?.city}, {shippingInput?.state}{" "}
                            {shippingInput?.zipCode}
                          </div>
                        </div>
                      )}
                    </PreviewContainer>
                  }
                >
                  <ShippingMethodForm />
                </CollapsibleSection>
              )}

              {/* Retailer Login */}
              {!payment.forceComplete &&
              retailerLogin.visible &&
              checkoutIntent?.retailerConnectionRequired !== false ? (
                <CollapsibleSection
                  hideControl={!retailerLogin.canOpen}
                  constantContent={true}
                  smoothAnimation={true}
                  onToggle={() => {
                    toggleSection("retailerLogin");
                  }}
                  isOpen={retailerLogin.open}
                  disabled={!retailerLogin.canOpen}
                  loading={loading}
                  title={"Delivery method"}
                  titleIcon={
                    <StatusIcon
                      success={!!checkoutIntent?.retailerConnection}
                      errorColor="rgb(157, 56, 65)"
                    />
                  }
                >
                  <AccountForCheckout />
                </CollapsibleSection>
              ) : null}
              <CollapsibleSection
                hideControl={true}
                isOpen={true}
                disabled={false}
                loading={false}
                title={"Order summary"}
                sx={{
                  display: "none",
                  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
                    display: "block",
                  },
                }}
              >
                <OrderSummary showOrderSummaryTitle={false} />
              </CollapsibleSection>
            </>
          ) : null}
        </div>
        <div className="sideSummary">
          <OrderSummary />
        </div>

        {<FloatingOrderSummary />}
      </Container>
    </LandingPageWrapper>
  );
};

export default CheckoutForm;
